import React, { useEffect, useState, useCallback } from "react";
import { Smartphone, X, Share, Plus, ChevronRight } from "lucide-react";

/**
 * "Add to Home Screen" PWA install helper.
 *  - On Chrome (Android & desktop) it catches the `beforeinstallprompt` event
 *    and offers a one-tap install via prompt().
 *  - On iOS Safari (which doesn't support beforeinstallprompt) it shows
 *    visual instructions: Share → Add to Home Screen.
 *  - Once the app is running as an installed PWA (display-mode: standalone),
 *    the helper hides itself entirely.
 *
 * Two render modes:
 *  - <InstallBanner /> — dashboard top banner, auto-hides 24h after first login
 *  - <InstallFooterLink /> — small persistent link in the sidebar footer
 */

function isStandalone() {
  if (typeof window === "undefined") return false;
  // iOS Safari
  if (window.navigator?.standalone === true) return true;
  // Chrome / Android
  return window.matchMedia?.("(display-mode: standalone)").matches === true;
}

function isIos() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isAndroid() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  return /Android/i.test(ua);
}

function detectPlatform() {
  if (isIos()) return "ios";
  if (isAndroid()) return "android";
  return "desktop";
}

function browserName() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/SamsungBrowser/i.test(ua)) return "Samsung Internet";
  if (/EdgA|Edg\//i.test(ua)) return "Edge";
  if (/FxiOS|Firefox/i.test(ua)) return "Firefox";
  if (/OPR|Opera/i.test(ua)) return "Opera";
  if (/CriOS|Chrome/i.test(ua)) return "Chrome";
  if (/Safari/i.test(ua)) return "Safari";
  return "your browser";
}

function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    if (installed) return;
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [installed]);

  const promptInstall = useCallback(async () => {
    if (!deferred) return null;
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice?.outcome;
  }, [deferred]);

  return { canPromptNow: !!deferred, installed, promptInstall };
}

function InstructionsModal({ platform, browser, onClose }) {
  const isIosM = platform === "ios";
  const isAndroidM = platform === "android";
  const title = isIosM ? "Install on iPhone / iPad"
    : isAndroidM ? "Install on Android"
    : "Install on desktop";
  return (
    <div data-testid="install-instructions-modal" data-platform={platform} onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 9999 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ background: "white", maxWidth: 460, width: "100%", padding: 24, position: "relative" }}>
        <button onClick={onClose} className="btn-ghost" style={{ position: "absolute", top: 12, right: 12 }} data-testid="install-instructions-close"><X size={14} /></button>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <Smartphone size={20} color="#ff5a00" />
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{title}</h2>
        </div>

        {isIosM ? (
          <>
            <p style={{ fontSize: 13, color: "#475569", marginBottom: 14 }}>
              iOS doesn&apos;t have a one-tap install. Follow these three steps in <strong>Safari</strong> (it must be Safari — Chrome on iOS won&apos;t work):
            </p>
            <ol style={{ paddingLeft: 22, fontSize: 13, color: "#0f172a" }}>
              <li style={{ marginBottom: 10 }}>
                Tap the <strong>Share</strong> button <Share size={14} style={{ verticalAlign: -2, marginLeft: 2 }} /> at the bottom of Safari.
              </li>
              <li style={{ marginBottom: 10 }}>
                Scroll down and tap <strong>Add to Home Screen</strong> <Plus size={14} style={{ verticalAlign: -2, marginLeft: 2 }} />.
              </li>
              <li>
                Confirm the name &quot;Newell&quot; and tap <strong>Add</strong>. The icon will appear on your home screen.
              </li>
            </ol>
          </>
        ) : isAndroidM ? (
          <>
            <p style={{ fontSize: 13, color: "#475569", marginBottom: 14 }}>
              Your browser ({browser}) didn&apos;t pop the auto-install dialog this time, so install it manually:
            </p>
            <ol style={{ paddingLeft: 22, fontSize: 13, color: "#0f172a" }}>
              <li style={{ marginBottom: 10 }}>
                Tap the <strong>⋮</strong> menu (top-right of the browser).
              </li>
              <li style={{ marginBottom: 10 }}>
                Choose <strong>Install app</strong> {browser === "Samsung Internet" ? <>or <strong>Add page to → Home screen</strong></> : <>(or <strong>Add to Home screen</strong> on older versions)</>}.
              </li>
              <li>
                Confirm and the Newell icon will appear on your home screen.
              </li>
            </ol>
            <div className="card" style={{ marginTop: 12, padding: 10, background: "#f1f5f9", color: "#475569", fontSize: 11, borderLeft: "3px solid #94a3b8" }}>
              Tip: if you don&apos;t see &quot;Install app&quot;, fully reload this page once — the browser only offers it after it confirms the site is a PWA.
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: "#475569", marginBottom: 14 }}>
              {browser} didn&apos;t pop the install dialog this time. You can still pin Newell to your dock or taskbar:
            </p>
            <ol style={{ paddingLeft: 22, fontSize: 13, color: "#0f172a" }}>
              <li style={{ marginBottom: 10 }}>
                Look in the URL bar for the install icon (a small monitor with a down-arrow), or open the <strong>⋮</strong> menu.
              </li>
              <li style={{ marginBottom: 10 }}>
                Choose <strong>Install Newell…</strong> (or <strong>Apps → Install this site as an app</strong> on Edge).
              </li>
              <li>
                Confirm — the app opens in its own window and you can pin it to your taskbar or dock.
              </li>
            </ol>
          </>
        )}

        <div className="card" style={{ marginTop: 14, padding: 10, background: "#fff7ed", color: "#7c2d12", fontSize: 11, borderLeft: "3px solid #f59e0b" }}>
          The installed PWA is what unlocks push notifications + lock-screen callout popups. Worth doing once per device.
        </div>
      </div>
    </div>
  );
}

const DISMISS_KEY = "newell:install_banner_dismissed_at";
const BANNER_WINDOW_HOURS = 24;

/** Dashboard top banner — visible to anyone whose device can install, for first 24h post-login. */
export function InstallBanner() {
  const { canPromptNow, installed, promptInstall } = useInstallPrompt();
  const [showHelp, setShowHelp] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const platform = detectPlatform();
  const browser = browserName();

  useEffect(() => {
    // Hide if previously dismissed within the window
    const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (at && Date.now() - at < BANNER_WINDOW_HOURS * 3600 * 1000) setDismissed(true);
  }, []);

  if (installed || dismissed) return null;
  // Hide only if we genuinely can't help: no native prompt AND not a known platform we have instructions for.
  if (!canPromptNow && platform === "desktop" && !navigator.userAgent) return null;

  const onInstall = async () => {
    if (canPromptNow) {
      const outcome = await promptInstall();
      if (outcome !== "accepted") {
        // dismissed via OS dialog → respect for the window
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setDismissed(true);
      }
    } else {
      setShowHelp(true);
    }
  };
  const onSkip = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <>
      <div data-testid="install-banner" className="card" style={{ padding: 12, marginBottom: 14, background: "#0f172a", color: "#fff", borderLeft: "4px solid #ff5a00", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Smartphone size={20} color="#ff5a00" />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Install Newell on this device</div>
          <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: 2 }}>Adds a home-screen icon, unlocks push notifications and the lock-screen callout alarm.</div>
        </div>
        <button data-testid="install-banner-install-btn" onClick={onInstall} className="btn-primary" style={{ background: "#ff5a00", color: "#fff", border: 0, padding: "8px 14px", fontWeight: 700 }}>
          Install <ChevronRight size={12} style={{ verticalAlign: -1 }} />
        </button>
        <button data-testid="install-banner-skip" onClick={onSkip} style={{ background: "transparent", color: "#cbd5e1", border: "1px solid #334155", padding: "8px 10px", fontSize: 11, borderRadius: 3 }}>Not now</button>
      </div>
      {showHelp ? <InstructionsModal platform={platform} browser={browser} onClose={() => setShowHelp(false)} /> : null}
    </>
  );
}

/** Small persistent "Install app" link for the sidebar footer.
 *  Per user request: ALWAYS visible (even when the PWA is already installed) so anyone
 *  re-installing on a new device can find the entry point. Shows above Sign out. */
export function InstallFooterLink() {
  const { canPromptNow, installed, promptInstall } = useInstallPrompt();
  const [showHelp, setShowHelp] = useState(false);
  const platform = detectPlatform();
  const browser = browserName();
  const onClick = async () => {
    if (canPromptNow) { await promptInstall(); }
    else { setShowHelp(true); }  // surface platform-appropriate instructions when no native prompt is available
  };
  return (
    <>
      <button data-testid="install-footer-link" onClick={onClick}
        title={installed ? "Already installed on this device — tap for instructions to install on another device" : "Add Newell to your home screen"}
        style={{ background: "transparent", color: "#94a3b8", border: 0, padding: "6px 8px", fontSize: 11, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", width: "100%", justifyContent: "center" }}>
        <Smartphone size={12} />{installed ? "Install on another device" : "Install app on this device"}
      </button>
      {showHelp ? <InstructionsModal platform={platform} browser={browser} onClose={() => setShowHelp(false)} /> : null}
    </>
  );
}
